import React from 'react'
import ValidatedDynamicFormField from './ValidatedDynamicFormField'
import { FormFieldMapping } from '../services/fieldMappingService'
import type { ValidationState } from '../../validators/types'

interface DynamicFormSectionProps {
  category: string
  fields: FormFieldMapping[]
  formData: { [key: string]: any }
  errors: { [key: string]: string }
  onChange: (fieldName: string, value: any) => void
  validationState?: ValidationState
  onValidation?: (fieldName: string, isValid: boolean, message?: string) => void
}

const DynamicFormSection: React.FC<DynamicFormSectionProps> = ({
  category,
  fields,
  formData,
  errors,
  onChange,
  validationState,
  onValidation
}) => {
  // Map Salesforce field types to form input types (same as DynamicFormField)
  const mapSalesforceTypeToInputType = (salesforceType: string): string => {
    switch (salesforceType) {
      case 'string':
      case 'text': 
        return 'text'
      case 'textarea':
        return 'textarea'
      case 'email':
        return 'email'
      case 'phone':
        return 'phone'
      case 'url':
        return 'url'
      case 'select':
      case 'picklist':
        return 'select'
      case 'boolean':
        return 'boolean'
      case 'date':
      case 'datetime':
        return 'date'
      case 'number':
      case 'double':
      case 'int':
        return 'number'
      case 'currency':
        return 'currency'
      case 'percent':
        return 'percentage'
      default:
        return 'text'
    }
  }

  // Get effective field type for a field
  const getEffectiveFieldType = (field: FormFieldMapping): string => {
    return field.salesforceFieldType 
      ? mapSalesforceTypeToInputType(field.salesforceFieldType)
      : field.formFieldType
  }

  // Get icon for category
  const getCategoryIcon = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'business':
        return 'bi-building'
      case 'business address':
        return 'bi-geo-alt'
      case 'primary contact':
        return 'bi-person'
      case 'contact address':
        return 'bi-house'
      case 'deal information':
        return 'bi-cash-coin'
      case 'general':
        return 'bi-info-circle'
      default:
        return 'bi-file-text'
    }
  }

  // Sort fields by display order
  const sortedFields = fields.sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder
    }
    return a.formFieldLabel.localeCompare(b.formFieldLabel)
  })

    return (
    <div className="submit-deal-form-section">
      {/* Spacer to align with sidebar header */}
      <div className="clean-form-spacer"></div>
      
      <div className="clean-form-layout">
        <div className="clean-form-sidebar">
          <h2 className="clean-form-sidebar-title">
            {category} 
          </h2>
          <p className="clean-form-sidebar-description">
            Please provide your {category.toLowerCase()} information to proceed.
          </p>
        </div>
        
        <div className="clean-form-container">
          <div className="clean-form-grid">
            {sortedFields.map((field) => (
              <div 
                key={field.formFieldName} 
                className={getEffectiveFieldType(field) === 'textarea' ? 'clean-form-field-full' : 'clean-form-field-half'}
              >
                <ValidatedDynamicFormField
                  field={field}
                  value={formData[field.formFieldName]}
                  onChange={onChange}
                  error={errors[field.formFieldName]}
                  validationState={validationState?.[field.formFieldName]}
                  onValidation={onValidation}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicFormSection
